import type React from "react";
import { type FC, useEffect, useRef, useState } from "react"
import ClearDataButton from "./ClearDataButton";
import { MapPinIcon } from "@heroicons/react/24/outline";
import useOutsideAlerter from "hooks/useOutsideAlerter";
import { ApisService } from "services/api";
import { useHotels } from "contexts/HotelsSearchContext/HotelProvider";
import { debounce } from "lodash";
import { HiOutlineOfficeBuilding } from "react-icons/hi";

export interface LocalStorageRecentSearches {
  id: number;
  name: string;
  type: "CITY" | "HOTEL";
  parent_id?: number;
}

interface LocationInputProps {
  onInputDone?: (value: string) => void;
  placeHolder?: string;
  desc?: string;
  className?: string;
  divHideVerticalLineClass?: string;
  autoFocus?: boolean;
}

export interface DestinationType {
  cities: City[]
  hotels: Hotel[]
}

export interface City {
  id: number
  name: string
  type: string
}

export interface Hotel {
  id: number
  name: string
  parent_id: number
  type: string
}

const LocationInput: FC<LocationInputProps> = ({
  autoFocus = false,
  onInputDone,
  placeHolder = "Destino",
  desc = "Para onde está indo?",
  className = "nc-flex-1.5",
  divHideVerticalLineClass = "left-10 -right-0.5",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedDestination, setSelectedDestination] = useState<LocalStorageRecentSearches | null>();
  const [value, setValue] = useState("");
  const [showPopover, setShowPopover] = useState(autoFocus);
  const [destinations, setDestinations] = useState<DestinationType | null>();

  const {
    setDestination,
    setShowHeroSearch,
    setDestinationName,
    destinationName,
    setGetHotelSearchFirst,
  } = useHotels();

  const storedDestinationsString = localStorage.getItem(
    "recentSearchesDestinations"
  );

  useEffect(() => {
    if (storedDestinationsString) {
      const localRecentSearchesDestinations: LocalStorageRecentSearches[] =
        JSON.parse(storedDestinationsString);
      const newRecentSearches = localRecentSearchesDestinations.filter(item => item.type);
      const searchTypeHotelNotParentId = localRecentSearchesDestinations.find(item => item.type === "HOTEL" && !item.parent_id);
      if (newRecentSearches.length === 0) {
        localStorage.removeItem("recentSearchesDestinations");
      }
      if (searchTypeHotelNotParentId) {
        const newRecentSearches = localRecentSearchesDestinations.filter(item => item.type !== "HOTEL" || item.parent_id);
        localStorage.setItem("recentSearchesDestinations", JSON.stringify(newRecentSearches));
      }
    }
  }, []);

  useEffect(() => {
    if (destinationName) {
      setValue(destinationName);
      if (inputRef.current) {
        inputRef.current.value = destinationName;
      }
    }
  }, [destinationName]);

  useEffect(() => {
    setShowPopover(autoFocus);
    if (autoFocus && !!inputRef.current) {
      setTimeout(() => {
        inputRef.current && inputRef.current.focus();
      }, 200);
    }
  }, [autoFocus]);

  useOutsideAlerter(containerRef, () => {
    setShowPopover(false);
  });

  useEffect(() => {
    if (showPopover && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showPopover]);

  const clearDataDestinations = () => {
    setDestinations(null);
    setValue("")
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }

  const debounceChange = debounce(e => {
    if (e.target.value.length >= 3) {
      setValue(e.target.value)
      ApisService.getDestinations(e.target.value).then((response: DestinationType) => {
        setDestinations(response);
      })
    } else {
      setDestinations(null);
    }
  }, 500);

  const handleChangeInputValue = (e: React.ChangeEvent<HTMLInputElement>) => debounceChange(e)

  const handleSelectDestination = (destination: LocalStorageRecentSearches, isRecentSearch?: boolean) => {
    // Configurações iniciais para destinos do tipo HOTEL ou outros
    setSelectedDestination(destination);
    if (destination.type === "HOTEL") {
      const hotelDestination = destination as Hotel;
      setDestination(hotelDestination.parent_id);
      setGetHotelSearchFirst(hotelDestination.id);
    } else {
      setDestination(destination.id);
    }

    // Atualiza o nome do destino e o valor do input
    setDestinationName(destination.name);
    setValue(destination.name);

    if (inputRef.current) {
      inputRef.current.value = destination.name;
    }

    // Chama o callback onInputDone se definido
    if (onInputDone) {
      onInputDone(destination.name);
    }

    // Obtém e atualiza buscas recentes no localStorage
    const storedDestinationsString = localStorage.getItem("recentSearchesDestinations");
    const localRecentSearchesDestinations: LocalStorageRecentSearches[] = storedDestinationsString ? JSON.parse(storedDestinationsString) : [];

    const newRecentSearches = localRecentSearchesDestinations.filter(item => item.id !== destination.id);

    const newDestination = {
      id: destination.id,
      name: destination.name,
      type: destination.type,
      ...(destination.type === "HOTEL" && { parent_id: (destination as Hotel).parent_id })
    };

    newRecentSearches.unshift(newDestination);

    if (newRecentSearches.length > 5) {
      newRecentSearches.pop();
    }

    localStorage.setItem("recentSearchesDestinations", JSON.stringify(newRecentSearches));

    // Configurações visuais
    setShowHeroSearch("dates");
    setShowPopover(false);
  };



  const renderRecentSearches = () => {
    const storedDestinationsString = localStorage.getItem(
      "recentSearchesDestinations"
    );

    const localRecentSearchesDestinations: LocalStorageRecentSearches[] =
      storedDestinationsString
        ? JSON.parse(storedDestinationsString)
        : undefined;

    return (
      <>
        <h3 className="block mt-2 sm:mt-0 px-4 sm:px-8 font-semibold text-base text-neutral-800 dark:text-neutral-100">
          Buscas recentes
        </h3>
        <div className="mt-2">
          {localRecentSearchesDestinations ? (
            localRecentSearchesDestinations.map((item: LocalStorageRecentSearches) => (
              <span
                onClick={() => handleSelectDestination(item, true)}
                key={item.id}
                className="flex px-4 sm:px-6 items-center space-x-3 py-4 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
              >
                <span className="block text-neutral-400">
                  {item.type === "CITY"
                    ? <MapPinIcon className="h-4 w-4 sm:h-6 sm:w-6" />
                    : <HiOutlineOfficeBuilding className="h-4 w-4 sm:h-6 sm:w-6" />
                  }
                </span>
                <span className="block text-neutral-700 dark:text-neutral-200">
                  {item.name}
                </span>
              </span>
            ))
          ) : (
            <p className="block mt-2 sm:mt-0 px-4 sm:px-8  text-base text-neutral-800 dark:text-neutral-100">
              Não há buscas recentes
            </p>
          )}
        </div>
      </>
    );
  };

  const renderSearchValue = () => {
    return (
      <>
        {destinations && destinations.cities.length > 0 &&
          destinations.cities.slice(0, 4).map((item) => (
            <span
              onClick={() => handleSelectDestination(item as unknown as LocalStorageRecentSearches)}
              key={item.id}
              className="flex px-4 sm:px-6 items-center space-x-3 py-4 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer overflow-y-auto"
            >
              <span className="block text-neutral-700 dark:text-neutral-200">
                {item.name}
              </span>
            </span>
          ))}
        {
          destinations && destinations.cities.length === 0 && destinations.hotels.length === 0 &&
          <span
            className="flex px-4 sm:px-6 items-center space-x-3 py-4"
          >
            <span className="block text-neutral-700 dark:text-neutral-200">
              Nenhum resultado para {value}
            </span>
          </span>
        }
      </>
    );
  };

  return (
    <div className={`relative flex ${className}`} ref={containerRef}>
      <div
        onClick={() => setShowPopover(true)}
        className={`flex flex-1 relative z-10 [ nc-hero-field-padding--small ] flex-shrink-0 items-center space-x-3 cursor-pointer focus:outline-none text-left ${showPopover ? "nc-hero-field-focused--2" : ""
          }`}
      >
        <div className="flex-1 flex items-center gap-2">
          {/* se localstorege o type */}
          {value && selectedDestination?.type === "HOTEL"
            ? <HiOutlineOfficeBuilding className="h-4 w-4 sm:h-6 sm:w-6 text-neutral-500 dark:text-neutral-400" />
            : <MapPinIcon className="h-4 w-4 sm:h-6 sm:w-6 text-neutral-500 dark:text-neutral-400" />
          }
          <div className="block dark:text-neutral-200">
            <input
              className={`block w-full bg-transparent border-none focus:ring-0 p-0 focus:outline-none focus:placeholder-neutral-400 xl:text-base font-semibold placeholder-neutral-800 dark:placeholder-neutral-200 truncate`}
              placeholder={placeHolder}
              autoFocus={showPopover}
              onChange={handleChangeInputValue}
              ref={inputRef}
            />
            <span className="block mt-0.5 text-sm text-neutral-500 dark:text-neutral-400 font-light ">
              <span className="line-clamp-1">{!!value ? placeHolder : desc}</span>
            </span>
            {value && showPopover && (
              <ClearDataButton onClick={clearDataDestinations} />
            )}
          </div>
        </div>
      </div>

      {showPopover && (
        <div
          className={`h-8 absolute self-center top-1/2 -translate-y-1/2 z-0 bg-white dark:bg-neutral-800 ${divHideVerticalLineClass}`}
        ></div>
      )}

      {showPopover && (
        <div className="absolute left-0 z-40 w-full min-w-[300px] sm:min-w-[400px] bg-white dark:bg-neutral-800 top-full mt-3 py-3 sm:py-5 rounded-3xl shadow-xl max-h-[600px] overflow-y-auto scrollbar-hide">
          {destinations && destinations.cities && destinations.cities.length > 0 && (
            <div className="flex items-center sm:px-6 mb-4 gap-1">
              <span className="text-neutral-500">
                <MapPinIcon className="h-4 w-4 sm:h-6 sm:w-6" />
              </span>
              <p className="h-4 w-4 sm:h-6 sm:w-6">
                Cidades
              </p>
            </div>
          )}
          {value && destinations ? renderSearchValue() : renderRecentSearches()}
          {destinations && destinations.hotels && destinations.hotels.length > 0 && (
            <div className="flex items-center sm:px-6 my-4 gap-1">
              <span className="text-neutral-500">
                <HiOutlineOfficeBuilding className="h-4 w-4 sm:h-6 sm:w-6" />
              </span>
              <p className="h-4 w-4 sm:h-6 sm:w-6">
                Hotéis
              </p>
            </div>
          )}
          {destinations?.hotels?.slice(0, 4).map((item) => (
            <span
              onClick={() => handleSelectDestination(item as unknown as LocalStorageRecentSearches)}
              key={item.id}
              className="flex px-4 sm:px-6 items-center space-x-3 py-4 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
            >
              <span className="block text-neutral-700 dark:text-neutral-200">
                {item.name}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationInput;
